import bearing from "@turf/bearing";
import destination from "@turf/destination";
import distance from "@turf/distance";
import { MAX_TRIANGLE_SIDE_LENGTH_METERS } from "@iventis/utilities";
import GeoJSON from "geojson";
import { FixedShapeSupportedGeometry } from "../types/internal";
import { ensure360Bearing, getCoordinateList, numbersEqualWithin, updateCoordinates } from "./geojson-helpers";

const TRIANGLE_ANGLES = [0, 120, 240] as const;

/** Creates regular triangle coordinates from a center and radius, returning 4 coordinates (3 + closing). */
export function createTriangleFromCenterAndRadius(center: GeoJSON.Position, radius: number, mapBearing = 0): GeoJSON.Position[] {
    const coords = TRIANGLE_ANGLES.map((angle) => destination(center, radius, ensure360Bearing(mapBearing + angle), { units: "meters" }).geometry.coordinates);
    return [...coords, coords[0]];
}

/** Gets the spherical center of a regular triangle from its three vertices. */
export function getTriangleCenter(geometry: FixedShapeSupportedGeometry): GeoJSON.Position {
    const coords = getCoordinateList(geometry);
    const uniqueCoords = coords.length > 1 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords.slice(0, -1) : coords;

    if (uniqueCoords.length <= 1) {
        return uniqueCoords[0] || [0, 0];
    }

    const summedVector = uniqueCoords.reduce(
        ([x, y, z], [longitude, latitude]) => {
            const longitudeRadians = (longitude * Math.PI) / 180;
            const latitudeRadians = (latitude * Math.PI) / 180;
            const latitudeScale = Math.cos(latitudeRadians);
            return [x + latitudeScale * Math.cos(longitudeRadians), y + latitudeScale * Math.sin(longitudeRadians), z + Math.sin(latitudeRadians)];
        },
        [0, 0, 0]
    );
    const [x, y, z] = summedVector;
    const longitude = (Math.atan2(y, x) * 180) / Math.PI;
    const latitude = (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI;
    return [longitude, latitude];
}

/** Creates triangle coordinates whose first edge has the requested geodesic length. */
function createTriangleFromCenterAndSideLength(center: GeoJSON.Position, sideLength: number, mapBearing = 0): GeoJSON.Position[] {
    let minimumRadius = 0;
    let maximumRadius = sideLength;

    // The planar radius (side / sqrt(3)) is only an approximation on the
    // Earth's surface. Find the radius which round-trips through the same Turf
    // geometry and distance calculation used when displaying the side length.
    for (let iteration = 0; iteration < 40; iteration += 1) {
        const radius = (minimumRadius + maximumRadius) / 2;
        const coords = createTriangleFromCenterAndRadius(center, radius, mapBearing);
        const calculatedSideLength = distance(coords[0], coords[1], { units: "meters" });
        if (calculatedSideLength < sideLength) {
            minimumRadius = radius;
        } else {
            maximumRadius = radius;
        }
    }

    return createTriangleFromCenterAndRadius(center, (minimumRadius + maximumRadius) / 2, mapBearing);
}

function createTriangleWithinSideLengthLimit(center: GeoJSON.Position, radius: number, mapBearing = 0): GeoJSON.Position[] {
    const coords = createTriangleFromCenterAndRadius(center, radius, mapBearing);
    return distance(coords[0], coords[1], { units: "meters" }) <= MAX_TRIANGLE_SIDE_LENGTH_METERS
        ? coords
        : createTriangleFromCenterAndSideLength(center, MAX_TRIANGLE_SIDE_LENGTH_METERS, mapBearing);
}

/** Gets one edge length of a regular triangle geometry in meters. */
export function getTriangleSideLength(geometry: FixedShapeSupportedGeometry): number {
    const coords = getCoordinateList(geometry);
    if (coords.length < 2) return 0;
    return distance(coords[0], coords[1], { units: "meters" });
}

/** Rotates a regular triangle without changing its center, side length, or area. */
export function rotateTriangle(geometry: FixedShapeSupportedGeometry, angle: number, newCenter?: GeoJSON.Position): FixedShapeSupportedGeometry {
    const center = getTriangleCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    if (!firstVertex) return geometry;

    const radius = distance(center, firstVertex, { units: "meters" });
    const currentBearing = ensure360Bearing(bearing(center, firstVertex));
    return updateCoordinates(geometry, createTriangleFromCenterAndRadius(newCenter ?? center, radius, currentBearing + angle));
}

/** Resizes a regular triangle during drawing - determines the radius from center to target coordinate. */
export function resizeTriangle(currentGeom: FixedShapeSupportedGeometry, targetCoord: GeoJSON.Position, mapBearing = 0): FixedShapeSupportedGeometry {
    const listOfCoordinates = getCoordinateList(currentGeom);
    if (currentGeom.coordinates.length === 0 || listOfCoordinates.every((coord) => numbersEqualWithin(targetCoord, coord, 0))) {
        return {
            ...currentGeom,
            coordinates: [currentGeom.type === "Polygon" ? [targetCoord, targetCoord] : targetCoord],
        } as FixedShapeSupportedGeometry;
    }

    const center = getTriangleCenter(currentGeom);
    const radius = distance(center, targetCoord, { units: "meters" });
    const coords = createTriangleWithinSideLengthLimit(center, radius, mapBearing);
    return updateCoordinates(currentGeom, coords) as FixedShapeSupportedGeometry;
}

/** Resizes a regular triangle by setting a new radius value while preserving current orientation. */
export function resizeTriangleByRadius(geometry: FixedShapeSupportedGeometry, newRadius: number): FixedShapeSupportedGeometry {
    const center = getTriangleCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    const bearingOfTriangle = firstVertex ? ensure360Bearing(bearing(center, firstVertex)) : 0;
    return updateCoordinates(geometry, createTriangleWithinSideLengthLimit(center, newRadius, bearingOfTriangle));
}

/** Resizes a regular triangle by setting a new side length value. */
export function resizeTriangleBySideLength(geometry: FixedShapeSupportedGeometry, newSideLength: number): FixedShapeSupportedGeometry {
    const center = getTriangleCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    const bearingOfTriangle = firstVertex ? ensure360Bearing(bearing(center, firstVertex)) : 0;
    const sideLength = Math.min(newSideLength, MAX_TRIANGLE_SIDE_LENGTH_METERS);
    return updateCoordinates(geometry, createTriangleFromCenterAndSideLength(center, sideLength, bearingOfTriangle));
}

/** Resizes a regular triangle when a node is dragged, preserving current triangle orientation. */
export function resizeTriangleFromNodeDrag(geometry: FixedShapeSupportedGeometry, newCoordinate: GeoJSON.Position): FixedShapeSupportedGeometry {
    const center = getTriangleCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    const bearingOfTriangle = firstVertex ? ensure360Bearing(bearing(center, firstVertex)) : 0;
    const newRadius = distance(center, newCoordinate, { units: "meters" });
    return updateCoordinates(geometry, createTriangleWithinSideLengthLimit(center, newRadius, bearingOfTriangle));
}
